import React, { useState, useMemo } from "react";
import { History, User, Clock, Info, ArrowRight, Search, Download, ChevronRight, ChevronDown, ChevronUp, AlertCircle, FileText, UserCheck, ShieldCheck, CreditCard, Layers, X, Filter, Calendar, Activity } from "lucide-react";
import { useTheme, fmtShort, fmt, Card, Btn, PageHeader, downloadAuditLog, Empty, ROLE_LABELS, getLocale } from "./shared";
import { S } from './strings';

// (2026-06-13) Читаемое представление before/after в аудите. Раньше объект
// (например контрагент {name, type, ...}) рендерился как «[object Object]».
function auditValToStr(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') return fmt(v);
  if (typeof v === 'boolean') return v ? S().common.yes : S().common.no;
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map(auditValToStr).join(', ');
  if (typeof v === 'object') {
    const parts = Object.entries(v)
      .filter(([, val]) => val !== null && val !== undefined && val !== '')
      .map(([k, val]) => `${k}: ${auditValToStr(val)}`);
    return parts.length ? parts.join(', ') : '—';
  }
  return String(v);
}

// Типы действий для фильтра (labels берутся из S())
const ACTION_MATCH = {
  all: { color: null },
  income: { color: 'accentGreen', match: ['income_create', 'income_debt'] },
  expense: { color: 'accentRed', match: ['expense_create', 'expense_debt'] },
  transfer: { color: 'accent', match: ['transfer_create', 'fund_transfer_create'] },
  accounts: { color: 'accentPurple', match: ['acc_create', 'acc_archive', 'fund_archive', 'fund_adjust'] },
  categories: { color: 'accentOrange', match: ['CAT_CREATE', 'CAT_UPDATE', 'CAT_ARCHIVE'] },
  users: { color: 'accentPurple', match: ['user_create', 'user_edit', 'user_delete', 'user_add_existing'] },
  debts: { color: 'accentOrange', match: ['debt_create', 'debt_delete', 'debt_edit', 'debt_archive'] },
  contractors: { color: 'accentYellow', match: ['contractor_create', 'contractor_update', 'contractor_archive'] },
  system: { color: 'textMuted', match: ['profile_update', 'system_import', 'UNDO_OP'] },
};

function getActionGroup(type) {
  if (!type) return 'system';
  for (const [key, val] of Object.entries(ACTION_MATCH)) {
    if (key === 'all') continue;
    if (val.match && val.match.some(m => type.toLowerCase().includes(m.toLowerCase()) || type === m)) return key;
  }
  return 'system';
}

function getActionColor(type, t) {
  const group = getActionGroup(type);
  const cfg = ACTION_MATCH[group];
  return cfg?.color ? t[cfg.color] : t.textMuted;
}

function getActionLabel(type) {
  const group = getActionGroup(type);
  return S().history.actionTypes[group] || type || '—';
}

// Пагинация
const PAGE_SIZE = 50;

export default function HistoryTab({ data, user, perms, isCompact }) {
  const t = useTheme();

  // Фильтры
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sel, setSel] = useState(null);
  const [page, setPage] = useState(0);
  const [sortDir, setSortDir] = useState('desc'); // desc = новые первые

  // Все уникальные пользователи из лога
  const allUsers = useMemo(() => {
    const map = new Map();
    (data.auditLog || []).forEach(l => {
      if (l.uid && l.uName && !map.has(l.uid)) map.set(l.uid, l.uName);
    });
    return [...map.entries()]; // [[uid, name], ...]
  }, [data.auditLog]);

  const logs = useMemo(() => {
    let list = data.auditLog || [];

    // Non-owners see only their own actions
    if (perms.seeHistory === 'own') {
      list = list.filter(l => l.uid === user.id);
    }

    // Фильтр по типу действия
    if (actionFilter !== 'all') {
      const cfg = ACTION_MATCH[actionFilter];
      if (cfg?.match) {
        list = list.filter(l => cfg.match.some(m => l.type?.toLowerCase().includes(m.toLowerCase()) || l.type === m));
      }
    }

    // Фильтр по пользователю
    if (userFilter !== 'all') {
      list = list.filter(l => l.uid === userFilter);
    }

    // Фильтр по дате
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      list = list.filter(l => new Date(l.date) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter(l => new Date(l.date) <= to);
    }

    // Текстовый поиск
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(l =>
        (l.uName || '').toLowerCase().includes(q) ||
        (l.desc || '').toLowerCase().includes(q) ||
        (l.details || '').toLowerCase().includes(q) ||
        (l.type || '').toLowerCase().includes(q)
      );
    }

    // Сортировка по дате
    list = [...list].sort((a, b) => {
      const da = new Date(a.date).getTime();
      const db = new Date(b.date).getTime();
      return sortDir === 'desc' ? db - da : da - db;
    });

    return list;
  }, [data.auditLog, actionFilter, userFilter, dateFrom, dateTo, search, sortDir, perms.seeHistory, user.id]);

  const totalPages = Math.ceil(logs.length / PAGE_SIZE);
  const paged = logs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Сброс страницы при смене фильтров
  const setFilter = (setter) => (val) => { setter(val); setPage(0); };

  const activeFiltersCount = [actionFilter !== 'all', userFilter !== 'all', dateFrom, dateTo].filter(Boolean).length;

  return (
    <div>
      <PageHeader
        title={S().history.pageTitle}
        sub={`${S().history.pageSubtitle} (${logs.length})`}
        right={
          <Btn variant="ghost" onClick={() => downloadAuditLog(data)} style={{ background: `${t.accentGreen}15`, color: t.accentGreen }}>
            <Download size={16} /> {S().history.exportTsv}
          </Btn>
        }
      />

      {/* Панель поиска и фильтров */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 250px', minWidth: 200 }}>
          <Search size={16} color={t.textMuted} style={{ position: 'absolute', left: 12, top: 11 }} />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder={S().history.searchPlaceholder}
            style={{ paddingLeft: 38, height: 40, width: '100%', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgInput, color: t.text, fontSize: 14 }}
          />
        </div>
        <button onClick={() => setShowFilters(!showFilters)} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
          background: activeFiltersCount > 0 ? `${t.accent}15` : t.bgInput,
          border: `1px solid ${activeFiltersCount > 0 ? t.accent : t.border}`,
          color: activeFiltersCount > 0 ? t.accent : t.textSec, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          <Filter size={15} />
          {activeFiltersCount > 0 ? `${activeFiltersCount}` : ''}
          {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Расширенные фильтры */}
      {showFilters && (
        <Card style={{ marginBottom: 14, padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr 1fr 1fr', gap: 12 }}>
            {/* Фильтр по действию */}
            <div>
              <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block', textTransform: 'uppercase' }}>
                <Activity size={12} style={{ verticalAlign: -1 }} /> {S().history.filterActionType}
              </label>
              <select value={actionFilter} onChange={e => setFilter(setActionFilter)(e.target.value)} style={{
                width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${t.border}`,
                background: t.bgInput, color: t.text, fontSize: 13,
              }}>
                {Object.keys(ACTION_MATCH).map(key => (
                  <option key={key} value={key}>{S().history.actionTypes[key]}</option>
                ))}
              </select>
            </div>

            {/* Фильтр по пользователю */}
            <div>
              <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block', textTransform: 'uppercase' }}>
                <User size={12} style={{ verticalAlign: -1 }} /> {S().history.filterUser}
              </label>
              <select value={userFilter} onChange={e => setFilter(setUserFilter)(e.target.value)} style={{
                width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${t.border}`,
                background: t.bgInput, color: t.text, fontSize: 13,
              }}>
                <option value="all">{S().history.filterAll}</option>
                {allUsers.map(([uid, name]) => (
                  <option key={uid} value={uid}>{name}</option>
                ))}
              </select>
            </div>

            {/* Дата от */}
            <div>
              <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block', textTransform: 'uppercase' }}>
                <Calendar size={12} style={{ verticalAlign: -1 }} /> {S().history.filterDateFrom}
              </label>
              <input type="date" value={dateFrom} onChange={e => setFilter(setDateFrom)(e.target.value)} style={{
                width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${t.border}`,
                background: t.bgInput, color: t.text, fontSize: 13,
              }} />
            </div>

            {/* Дата до */}
            <div>
              <label style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, marginBottom: 4, display: 'block', textTransform: 'uppercase' }}>
                <Calendar size={12} style={{ verticalAlign: -1 }} /> {S().history.filterDateTo}
              </label>
              <input type="date" value={dateTo} onChange={e => setFilter(setDateTo)(e.target.value)} style={{
                width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${t.border}`,
                background: t.bgInput, color: t.text, fontSize: 13,
              }} />
            </div>
          </div>

          {activeFiltersCount > 0 && (
            <button onClick={() => { setActionFilter('all'); setUserFilter('all'); setDateFrom(''); setDateTo(''); setPage(0); }}
              style={{ marginTop: 10, background: 'none', border: 'none', color: t.accentRed, fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
              <X size={12} style={{ verticalAlign: -2 }} /> {S().history.resetFilters}
            </button>
          )}
        </Card>
      )}

      {/* ТАБЛИЦА */}
      <Card style={{ padding: 0, overflow: 'auto' }}>
        {logs.length === 0 ? (
          <Empty />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: t.bgInput, borderBottom: `2px solid ${t.border}` }}>
                <th style={{ ...thStyle(t), cursor: 'pointer', userSelect: 'none' }} onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {S().history.colDateTime}
                    {sortDir === 'desc' ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
                  </div>
                </th>
                <th style={thStyle(t)}>{S().history.colUser}</th>
                <th style={thStyle(t)}>{S().history.colType}</th>
                <th style={thStyle(t)}>{S().history.colAction}</th>
                <th style={thStyle(t)}>{S().history.colChanges}</th>
                <th style={thStyle(t)}>{S().history.colDetails}</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((l) => (
                <LogRow key={l.id} l={l} t={t} onClick={() => setSel(l)} />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={pgBtnStyle(t, page === 0)}>&#8592;</button>
          <span style={{ fontSize: 13, color: t.textSec }}>{page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} style={pgBtnStyle(t, page >= totalPages - 1)}>&#8594;</button>
        </div>
      )}

      {sel && <LogModal l={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

// ─── Стили ───

const thStyle = (t) => ({
  padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 800,
  color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.5,
  whiteSpace: 'nowrap', borderBottom: `1px solid ${t.border}`,
});

const pgBtnStyle = (t, disabled) => ({
  padding: '6px 14px', borderRadius: 8, border: `1px solid ${t.border}`,
  background: disabled ? t.bgInput : t.bgCard, color: disabled ? t.textMuted : t.text,
  cursor: disabled ? 'default' : 'pointer', fontSize: 14, fontWeight: 700,
  opacity: disabled ? 0.5 : 1,
});

// ─── Строка таблицы ───

function LogRow({ l, t, onClick }) {
  const actionColor = getActionColor(l.type, t);
  const d = new Date(l.date);
  const loc = getLocale();
  const dateStr = d.toLocaleDateString(loc, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' });

  const hasChanges = l.before !== undefined && l.before !== null && l.after !== undefined && l.after !== null;

  return (
    <tr onClick={onClick} style={{ cursor: 'pointer', borderBottom: `1px solid ${t.border}` }}
      onMouseEnter={e => e.currentTarget.style.background = t.bgInput}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {/* Дата / Время */}
      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{dateStr}</div>
        <div style={{ fontSize: 11, color: t.textMuted }}>{timeStr}</div>
      </td>

      {/* Пользователь */}
      <td style={{ padding: '10px 14px', verticalAlign: 'top' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{l.uName}</div>
        <span style={{
          fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 800, textTransform: 'uppercase',
          background: `${l.uRole === 'owner' ? t.accentPurple : l.uRole === 'admin' ? t.accentYellow : t.accentGreen}15`,
          color: l.uRole === 'owner' ? t.accentPurple : l.uRole === 'admin' ? t.accentYellow : t.accentGreen,
        }}>
          {ROLE_LABELS[l.uRole] || l.uRole}
        </span>
      </td>

      {/* Тип */}
      <td style={{ padding: '10px 14px', verticalAlign: 'top' }}>
        <span style={{
          fontSize: 11, padding: '3px 8px', borderRadius: 6, fontWeight: 700,
          background: `${actionColor}15`, color: actionColor, whiteSpace: 'nowrap',
        }}>
          {getActionLabel(l.type)}
        </span>
      </td>

      {/* Действие */}
      <td style={{ padding: '10px 14px', verticalAlign: 'top', maxWidth: 300 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: t.text, lineHeight: 1.4 }}>{l.desc}</div>
      </td>

      {/* Изменения (было → стало) */}
      <td style={{ padding: '10px 14px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        {hasChanges ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ color: t.accentRed, fontWeight: 700 }}>{auditValToStr(l.before)}</span>
            <ArrowRight size={12} color={t.textMuted} />
            <span style={{ color: t.accentGreen, fontWeight: 700 }}>{auditValToStr(l.after)}</span>
          </div>
        ) : (
          <span style={{ color: t.textMuted, fontSize: 11 }}>—</span>
        )}
      </td>

      {/* Детали */}
      <td style={{ padding: '10px 14px', verticalAlign: 'top', maxWidth: 200, fontSize: 12, color: t.textSec }}>
        {l.details ? (
          <div style={{ lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {l.details}
          </div>
        ) : '—'}
      </td>
    </tr>
  );
}

// ─── Модалка деталей ───

function LogModal({ l, onClose }) {
  const t = useTheme();
  const hasChanges = l.before !== undefined && l.before !== null && l.after !== undefined && l.after !== null;

  const renderVal = (v) => {
    if (typeof v === 'number') return <span className="mono" style={{ fontWeight: 800 }}>{fmt(v)}</span>;
    if (typeof v === 'boolean') return v ? S().common.yes : S().common.no;
    if (!v) return <span style={{ fontStyle: 'italic', color: t.textMuted }}>—</span>;
    return <span>{auditValToStr(v)}</span>;
  };

  const d = new Date(l.date);
  const loc = getLocale();
  const fullDate = d.toLocaleDateString(loc, { day: '2-digit', month: 'long', year: 'numeric' }) + ', ' +
    d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <Card style={{ width: 520, maxWidth: '95vw', padding: 0, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <History size={20} color={t.accent} />
            <div style={{ fontSize: 17, fontWeight: 800 }}>{S().history.eventDetails}</div>
          </div>
          <button onClick={onClose} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}><X size={18} /></button>
        </div>

        <div style={{ padding: 24, display: 'grid', gap: 16 }}>
          {/* Дата */}
          <div style={{ display: 'flex', gap: 14 }}>
            <div style={{ padding: 10, background: t.bgInput, borderRadius: 12 }}><Clock size={18} color={t.textMuted} /></div>
            <div>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 2 }}>{S().history.dateTime}</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{fullDate}</div>
            </div>
          </div>

          {/* Пользователь */}
          <div style={{ display: 'flex', gap: 14 }}>
            <div style={{ padding: 10, background: t.bgInput, borderRadius: 12 }}><User size={18} color={t.textMuted} /></div>
            <div>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 2 }}>{S().history.executor}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{l.uName}</span>
                <span style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 800,
                  background: `${l.uRole === 'owner' ? t.accentPurple : t.accentGreen}15`,
                  color: l.uRole === 'owner' ? t.accentPurple : t.accentGreen,
                }}>
                  {ROLE_LABELS[l.uRole] || l.uRole}
                </span>
              </div>
            </div>
          </div>

          {/* Тип + Действие */}
          <div style={{ padding: 16, background: `${t.accent}08`, borderRadius: 12, border: `1px solid ${t.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, fontWeight: 700, background: `${getActionColor(l.type, t)}15`, color: getActionColor(l.type, t) }}>
                {getActionLabel(l.type)}
              </span>
              <span style={{ fontSize: 11, color: t.textMuted }}>{l.type}</span>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{l.desc}</div>
            {l.details && <div style={{ fontSize: 13, color: t.textSec, marginTop: 8, padding: 10, background: t.bgInput, borderRadius: 8, lineHeight: 1.5 }}>{l.details}</div>}
          </div>

          {/* Изменения */}
          {hasChanges && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10, color: t.textMuted }}>{S().history.stateChanges}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 16, alignItems: 'center' }}>
                <div style={{ padding: 14, background: t.bgInput, borderRadius: 12, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: t.accentRed, fontWeight: 800, marginBottom: 4, textTransform: 'uppercase' }}>{S().history.wasBefore}</div>
                  <div style={{ fontSize: 16 }}>{renderVal(l.before)}</div>
                </div>
                <ArrowRight size={20} color={t.textMuted} />
                <div style={{ padding: 14, background: `${t.accentGreen}10`, borderRadius: 12, textAlign: 'center', border: `1px solid ${t.accentGreen}20` }}>
                  <div style={{ fontSize: 10, color: t.accentGreen, fontWeight: 800, marginBottom: 4, textTransform: 'uppercase' }}>{S().history.becameAfter}</div>
                  <div style={{ fontSize: 16, color: t.accentGreen }}>{renderVal(l.after)}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '16px 24px', background: t.bgInput, textAlign: 'right' }}>
          <Btn onClick={onClose}>{S().history.understood}</Btn>
        </div>
      </Card>
    </div>
  );
}
